// 日々の仕組み：ログインボーナス（7日周期・累計日数）、デイリー/ウィークリー任務（固定テンプレート×日付シード）、
// 本日初出撃3戦のドロップ2倍、寄進（全員EXP+20%を30分）、EXP倍率。
import { randInt, rngFrom, seedOf, shuffle } from '../rng';
import { CONFIG } from './config';
import { gainExp } from './level';
import { hasEntitlement, type Bundle, type OpResult, type Profile, type Resources } from './profile';
import { addStamina } from './stamina';

type LoginReward = Partial<Record<'zeni' | 'reidama' | 'zaimoku' | 'hyoro' | 'fuda' | 'kuji' | 'soutou' | 'exp', number>>;

export const LOGIN_BONUS = {
  get cycle(): LoginReward[] {
    return CONFIG.login.cycle;
  },
  get milestoneEvery() {
    return CONFIG.login.milestoneEvery;
  },
  get milestone(): LoginReward {
    return CONFIG.login.milestone;
  },
};

/** 累計 days 日目（1始まり）の報酬 */
export function loginReward(days: number): LoginReward {
  const c = CONFIG.login;
  const out: LoginReward = { ...c.cycle[(days - 1) % c.cycle.length] };
  if (days % c.milestoneEvery === 0) for (const [k, v] of Object.entries(c.milestone)) out[k as keyof LoginReward] = (out[k as keyof LoginReward] ?? 0) + v!;
  return out;
}

function applyLoginReward(p: Profile, res: Resources, r: LoginReward, now: number, dayKey: string) {
  if (r.zeni) res.zeni += r.zeni;
  if (r.reidama) res.reidama += r.reidama;
  if (r.zaimoku) res.zaimoku += r.zaimoku;
  if (r.fuda) res.fuda += r.fuda;
  if (r.kuji) res.kuji = (res.kuji ?? 0) + r.kuji;
  if (r.hyoro) addStamina(p, now, r.hyoro);
  if (r.exp) gainExp(p, res, r.exp, r.exp, now, dayKey);
}

/** その日の初回ログイン。累計日数を進めて報酬を渡す */
export function claimLogin(p: Profile, res: Resources, dayKey: string, now: number): OpResult<{ days: number; reward: LoginReward }> {
  if (p.login.last === dayKey) return { ok: false, error: 'claimed' };
  p.login.days++;
  p.login.last = dayKey;
  const reward = loginReward(p.login.days);
  applyLoginReward(p, res, reward, now, dayKey);
  // 掃討チケットは毎日（周期の報酬とは別に。所持の上限まで）
  const soutou = grantSweepTickets(res, CONFIG.sweep.ticketsDaily);
  return { ok: true, days: p.login.days, reward: soutou ? { ...reward, soutou } : reward };
}

/** 掃討チケットを配る（所持の上限 sweep.ticketCap まで。超えた分は配らない）。実際に増えた数を返す */
export function grantSweepTickets(res: Resources, n: number): number {
  const have = res.soutou ?? 0;
  const add = Math.max(0, Math.min(n, CONFIG.sweep.ticketCap - have));
  res.soutou = have + add;
  return add;
}

// ---- 任務 ----

export type MissionKind = 'win' | 'battle' | 'sweep' | 'build' | 'hyoro' | 'enhance' | 'village' | 'kills' | 'stars' | 'gacha';

interface MissionTemplate {
  kind: MissionKind;
  text: string; // {n} に目標数
  targets: number[];
  reward: Bundle;
}

export const DAILY_TEMPLATES: MissionTemplate[] = [
  { kind: 'win', text: '戦闘に{n}回勝つ', targets: [3, 5], reward: { reidama: 20 } },
  { kind: 'battle', text: '{n}回出撃する（掃討を含む）', targets: [10, 20], reward: { zeni: 1500 } },
  { kind: 'build', text: '建物を{n}回建てる', targets: [5, 10], reward: { zaimoku: 20 } },
  { kind: 'hyoro', text: '兵糧を{n}使う', targets: [60, 90], reward: { reidama: 20 } },
  { kind: 'enhance', text: '装備を{n}回強化する', targets: [1, 3], reward: { zeni: 1000 } },
  { kind: 'village', text: '里で{n}回受け取る', targets: [1, 2], reward: { zaimoku: 15 } },
  { kind: 'kills', text: '妖怪を{n}体倒す', targets: [20, 40], reward: { reidama: 20 } },
];
export const WEEKLY_TEMPLATES: MissionTemplate[] = [
  { kind: 'win', text: '戦闘に{n}回勝つ', targets: [40, 60], reward: { kuji: 4 } },
  { kind: 'stars', text: '星を{n}個取る', targets: [5, 10], reward: { kuji: 4 } },
  { kind: 'sweep', text: '{n}回掃討する', targets: [100, 200], reward: { zeni: 10000 } },
  { kind: 'build', text: '建物を{n}回建てる', targets: [60, 100], reward: { zaimoku: 150 } },
  { kind: 'gacha', text: 'おまもりガチャを{n}回引く', targets: [10, 20], reward: { fuda: 1 } },
  { kind: 'enhance', text: '装備を{n}回強化する', targets: [10, 20], reward: { zeni: 8000 } },
];
/** 日替わり任務の全達成（霊玉30 → ガチャ券2。霊玉は式神Lv だけに使う。BALANCE.md 28章） */
export const DAILY_ALL_CLEAR: Bundle = { kuji: 2 };

export interface Mission {
  id: string; // 'd:win' / 'w:stars'
  kind: MissionKind;
  text: string;
  target: number;
  reward: Bundle;
}

function gen(prefix: 'd' | 'w', templates: MissionTemplate[], seed: string, n: number): Mission[] {
  const h = rngFrom(seedOf(seed));
  return shuffle(h, templates)
    .slice(0, n)
    .map((t) => {
      const target = t.targets[randInt(h, t.targets.length)];
      return { id: `${prefix}:${t.kind}`, kind: t.kind, text: t.text.replace('{n}', String(target)), target, reward: t.reward };
    });
}

/** dateSeed は 'YYYY-MM-DD' */
export const dailyMissions = (dateSeed: string) => gen('d', DAILY_TEMPLATES, `daily:${dateSeed}`, 3);
/** weekSeed は週の月曜の 'YYYY-MM-DD' など */
export const weeklyMissions = (weekSeed: string) => gen('w', WEEKLY_TEMPLATES, `weekly:${weekSeed}`, 3);

function roll(p: Profile, dayKey: string, weekKey: string) {
  if (p.daily.day !== dayKey) p.daily = { day: dayKey, sorties: 0, missions: {}, claimed: [] };
  if (p.weekly.week !== weekKey) p.weekly = { week: weekKey, missions: {}, claimed: [] };
}

/** 任務の進み具合を記録 */
export function trackMission(p: Profile, dayKey: string, weekKey: string, kind: MissionKind, amount = 1) {
  roll(p, dayKey, weekKey);
  p.daily.missions[kind] = (p.daily.missions[kind] ?? 0) + amount;
  p.weekly.missions[kind] = (p.weekly.missions[kind] ?? 0) + amount;
}

/** 達成した任務の報酬を受け取る。missionId が 'd:all' なら当日の3つ全部の追加報酬 */
export function claimMission(p: Profile, res: Resources, dayKey: string, weekKey: string, missionId: string): OpResult<{ reward: Bundle }> {
  roll(p, dayKey, weekKey);
  const daily = missionId.startsWith('d:');
  const box = daily ? p.daily : p.weekly;
  if (box.claimed.includes(missionId)) return { ok: false, error: 'claimed' };
  let reward: Bundle;
  if (missionId === 'd:all') {
    const ids = dailyMissions(dayKey).map((m) => m.id);
    if (!ids.every((id) => p.daily.claimed.includes(id))) return { ok: false, error: 'incomplete' };
    reward = DAILY_ALL_CLEAR;
    box.claimed.push(missionId);
    for (const [k, v] of Object.entries(reward)) (res as any)[k] = ((res as any)[k] ?? 0) + v;
    // 全達成は掃討チケットも（上限まで）
    const soutou = grantSweepTickets(res, CONFIG.sweep.ticketsAllClear);
    return { ok: true, reward: soutou ? { ...reward, soutou } : reward };
  } else {
    const m = (daily ? dailyMissions(dayKey) : weeklyMissions(weekKey)).find((x) => x.id === missionId);
    if (!m) return { ok: false, error: 'unknown' };
    if ((box.missions[m.kind] ?? 0) < m.target) return { ok: false, error: 'incomplete' };
    reward = m.reward;
  }
  box.claimed.push(missionId);
  for (const [k, v] of Object.entries(reward)) (res as any)[k] = ((res as any)[k] ?? 0) + v;
  return { ok: true, reward };
}

// ---- 本日初出撃（3戦ドロップ2倍） ----

/** 出撃を1回数え、その戦闘のドロップ倍率を返す（その日の最初の3戦は2） */
export function noteSortie(p: Profile, dayKey: string): number {
  if (p.daily.day !== dayKey) p.daily = { day: dayKey, sorties: 0, missions: {}, claimed: [] };
  p.daily.sorties++;
  return p.daily.sorties <= CONFIG.rewards.firstSortieBattles ? 2 : 1;
}

// ---- 寄進・EXP倍率 ----

export const KISHIN = {
  get expBonus() {
    return CONFIG.kishin.expBonus;
  },
  get minutes() {
    return CONFIG.kishin.minutes;
  },
  get dailyCap() {
    return CONFIG.kishin.dailyCap;
  },
  get hyoro() {
    return CONFIG.kishin.hyoro;
  },
  get hyoroHours() {
    return CONFIG.kishin.hyoroHours;
  },
  get hyoroPerDay() {
    return CONFIG.kishin.hyoroPerDay;
  },
};

/** 寄進が1件入ったあとの「全員EXP+20%」の終了時刻（重ねると延長） */
export const kishinUntil = (currentUntil: number, now: number) => Math.max(currentUntil, now) + CONFIG.kishin.minutes * 60_000;

/** EXPの倍率。基本EXPに対する足し算：EXP2倍（課金）で基本ぶんがもう1つ、寄進の効果中で基本の+20%（2倍は寄進のボーナスに乗らない）。上乗せ分は霊玉換算に含めない */
export function expMultiplier(p: Profile, opts: { kishinActive?: boolean } = {}): number {
  return (hasEntitlement(p, 'exp2x') ? CONFIG.paid.exp2xMult : 1) + (opts.kishinActive ? CONFIG.kishin.expBonus : 0);
}
